import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, BellIcon, Loader2Icon, CheckIcon } from 'lucide-react';
interface NudgeModalProps {
  educator: {
    id: number;
    name: string;
    email: string;
  };
  onClose: () => void;
  onSend: (message: string) => Promise<void>;
}
export function NudgeModal({
  educator,
  onClose,
  onSend
}: NudgeModalProps) {
  const [message, setMessage] = useState(`Hi ${educator.name.split(' ')[0]},\n\nJust checking in! We noticed you haven't shared any assessments this month yet. If you need any support or have questions, we're here to help.\n\nLooking forward to seeing your contributions!\n\nBest regards`);
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const handleSend = async () => {
    setIsSending(true);
    try {
      await onSend(message);
      setIsSent(true);
      // Close modal after showing success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to send nudge:', error);
      setIsSending(false);
    }
  };
  return <AnimatePresence data-id="element-1849">
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4" data-id="element-1850">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-surface-elevated rounded-2xl shadow-2xl max-w-2xl w-full border border-custom overflow-hidden" data-id="element-1851">
          {/* Header */}
          <div className="p-6 border-b border-custom" data-id="element-1852">
            <div className="flex items-start justify-between" data-id="element-1853">
              <div className="flex items-start gap-4" data-id="element-1854">
                <div className="w-12 h-12 rounded-xl bg-[#FF9D33] bg-opacity-10 flex items-center justify-center flex-shrink-0" data-id="element-1855">
                  <BellIcon size={24} className="text-[#FF9D33]" data-id="element-1856" />
                </div>
                <div data-id="element-1857">
                  <h2 className="text-xl font-bold text-text-primary mb-1" data-id="element-1858">
                    Send Reminder
                  </h2>
                  <p className="text-sm text-text-secondary" data-id="element-1859">
                    To {educator.name} ({educator.email})
                  </p>
                </div>
              </div>
              <button onClick={onClose} disabled={isSending} className="p-2 rounded-lg hover:bg-surface transition-colors disabled:opacity-50" data-id="element-1860">
                <XIcon size={20} className="text-text-secondary" data-id="element-1861" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6" data-id="element-1862">
            {isSent ? <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="py-12 text-center" data-id="element-1863">
                <div className="w-16 h-16 bg-[#00B75F] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1864">
                  <CheckIcon size={32} className="text-[#00B75F]" data-id="element-1865" />
                </div>
                <h3 className="text-lg font-bold text-text-primary mb-2" data-id="element-1866">
                  Reminder Sent!
                </h3>
                <p className="text-sm text-text-secondary" data-id="element-1867">
                  {educator.name} will receive your message shortly
                </p>
              </motion.div> : <>
                <label className="block text-sm font-semibold text-text-secondary uppercase tracking-wide mb-3" data-id="element-1868">
                  Message
                </label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={8} disabled={isSending} className="w-full px-4 py-3 bg-surface border-2 border-custom rounded-xl text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-[#FF9D33] focus:border-transparent resize-none disabled:opacity-50" placeholder="Write a supportive message..." data-id="element-1869" />

                {/* Helper Text */}
                <div className="mt-4 p-4 bg-[#2563EB] bg-opacity-5 rounded-xl border border-[#2563EB] border-opacity-20" data-id="element-1870">
                  <p className="text-xs text-text-secondary leading-relaxed" data-id="element-1871">
                    <strong className="text-text-primary" data-id="element-1872">Tip:</strong> Keep the
                    tone supportive and helpful. Focus on offering assistance
                    rather than criticism.
                  </p>
                </div>
              </>}
          </div>

          {/* Footer */}
          {!isSent && <div className="p-6 bg-surface border-t border-custom flex items-center justify-end gap-3" data-id="element-1873">
              <button onClick={onClose} disabled={isSending} className="px-6 py-3 border-2 border-custom text-text-primary rounded-xl font-semibold hover:bg-surface transition-colors disabled:opacity-50" data-id="element-1874">
                Cancel
              </button>
              <button onClick={handleSend} disabled={isSending || !message.trim()} className="px-6 py-3 bg-[#FF9D33] text-white rounded-xl font-semibold hover:bg-[#FF8C1A] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-1875">
                {isSending ? <>
                    <Loader2Icon size={18} className="animate-spin" data-id="element-1876" />
                    Sending...
                  </> : <>
                    <BellIcon size={18} data-id="element-1877" />
                    Send Reminder
                  </>}
              </button>
            </div>}
        </motion.div>
      </div>
    </AnimatePresence>;
}